import { Platform } from 'react-native';

/**
 * Homestead palette. Ported from the canonical HTML mockups
 * (homestead-onboarding-mockup.html, homestead-this-week-mockup.html).
 * Warm, calm, ADHD-friendly — no harsh red, no nagging tones.
 */
export const Palette = {
  sand: '#F1E8D8',
  sandDeep: '#E7DAC3',
  cream: '#FBF6EA',
  card: '#FCF8EF',

  ink: '#2A251F',
  inkSoft: '#776E5F',
  inkFaint: '#A79C88',

  forest: '#2C4738',
  forestLight: '#3B5C4A',
  forestGlow: '#52765F',

  honey: '#E1A33D',
  honeyDeep: '#C2842A',
  honeySoft: '#F3DDAE',

  clay: '#B05F3C',
  moss: '#5C7E55',
  /** Muted heritage blue — the one cool tone in the warm palette.
   *  Reserved for hero accents that need to pop against forest/honey
   *  (Smart scan's icon chip, Wave 60). Desaturated so it reads
   *  "lake at dusk," not "tech startup." */
  lake: '#41637A',
  lakeDeep: '#33505F',
  line: '#E3D7BF',
} as const;

export type PaletteColor = keyof typeof Palette;

/**
 * Semantic light/dark themes. Dark variant is intentionally light-mode-leaning
 * for now — Homestead's identity is warm-light; a true dark theme is future work.
 */
export const Colors = {
  light: {
    background: Palette.sand,
    surface: Palette.card,
    surfaceMuted: Palette.sandDeep,
    text: Palette.ink,
    textSecondary: Palette.inkSoft,
    textFaint: Palette.inkFaint,
    primary: Palette.forest,
    primaryDeep: Palette.forestLight,
    primaryGlow: Palette.forestGlow,
    accent: Palette.honey,
    accentDeep: Palette.honeyDeep,
    accentSoft: Palette.honeySoft,
    line: Palette.line,
    clay: Palette.clay,
    moss: Palette.moss,
  },
  dark: {
    background: Palette.sand,
    surface: Palette.card,
    surfaceMuted: Palette.sandDeep,
    text: Palette.ink,
    textSecondary: Palette.inkSoft,
    textFaint: Palette.inkFaint,
    primary: Palette.forest,
    primaryDeep: Palette.forestLight,
    primaryGlow: Palette.forestGlow,
    accent: Palette.honey,
    accentDeep: Palette.honeyDeep,
    accentSoft: Palette.honeySoft,
    line: Palette.line,
    clay: Palette.clay,
    moss: Palette.moss,
  },
} as const;

export type ThemeColor = keyof typeof Colors.light;

/**
 * Type names match the loaded @expo-google-fonts packages
 * (see src/app/_layout.tsx). Use Fraunces for headings (italic
 * spans for emphasis), Hanken Grotesk for everything else.
 */
export const Fonts = {
  serif: 'Fraunces_500Medium',
  serifBold: 'Fraunces_600SemiBold',
  serifItalic: 'Fraunces_500Medium_Italic',
  sans: 'HankenGrotesk_400Regular',
  sansMedium: 'HankenGrotesk_500Medium',
  sansSemibold: 'HankenGrotesk_600SemiBold',
  sansBold: 'HankenGrotesk_700Bold',
} as const;

export const Spacing = {
  half: 2,
  one: 4,
  two: 8,
  three: 12,
  four: 16,
  five: 20,
  six: 24,
  seven: 32,
  eight: 40,
  ten: 56,
} as const;

export const Radius = {
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  '2xl': 24,
  '3xl': 30,
  pill: 999,
} as const;

export const BottomTabInset = Platform.select({ ios: 50, android: 80, default: 50 });
export const MaxContentWidth = 480;
